import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { FormattedDateComponentProps } from "../types";

/**
 * FormattedDateComponent - renders a formatted date.
 * Supports static value from props or dynamic interpolation from data.value.
 */
function FormattedDateComponent({
  value: valueProp,
  format: formatStr,
  data,
}: FormattedDateComponentProps) {
  const { t } = useTranslation();

  // Prioritize data.value (dynamic from API) over valueProp (static from schema)
  // The schema's default value is just a placeholder and should be overridden by actual data
  const rawValue =
    data != null && "value" in data && data.value != null && data.value !== ""
      ? data.value
      : valueProp != null && valueProp !== ""
        ? valueProp
        : null;

  if (rawValue == null || rawValue === "") {
    return <span>{t("common.never", { defaultValue: "Never" })}</span>;
  }

  try {
    // Schema data arrives as JSON, so a date is always a string (or an epoch number) — never a
    // Date instance.
    const dateValue =
      typeof rawValue === "string" || typeof rawValue === "number"
        ? new Date(rawValue)
        : null;

    if (dateValue && !Number.isNaN(dateValue.getTime())) {
      // Validate that the date is reasonable (not a default/minimum date)
      // Check if year is >= 2000 to filter out invalid dates like "0001-01-01T00:00:00"
      const year = dateValue.getFullYear();
      if (Number.isNaN(year) || year < 2000) {
        return <span>{t("common.never", { defaultValue: "Never" })}</span>;
      }
      
      const formatted = formatStr
        ? format(dateValue, formatStr)
        : format(dateValue, "PPp");
      return <span>{formatted}</span>;
    }
  } catch {
    // fallthrough
  }

  return <span>{String(rawValue)}</span>;
}

export { FormattedDateComponent };
